<i18n>
{
  "ja": {
    "warehouseLocation": "倉庫所在地",
    "warehouseLocationFormatted": "{location}の倉庫",
    "basicInfo": "基本情報",
    "operatingHours": "営業時間",
    "locationByCar": "車での立地",
    "warehouseType": "倉庫種別",
    "interchange": "高速IC",
    "airport": "空港",
    "seaport": "港",
    "station": "駅",
    "minFromOrigin": "{name}から{min}分",
    "warehouseSpecs": "倉庫スペック",
    "warehouseArea": "倉庫エリア",
    "goods": "貨物",
    "deliveryService": "配送サービス",
    "warehouseEquipmentsAndOps": "倉庫設備＆運営",
    "warehouseEquipments": "倉庫設備",
    "availableTruckSizes": "対応可能トラックサイズ",
    "security": "安全面",
    "counterDisaster": "災害対応",
    "holiday": "休日",
    "noValidAddress": "有効なアドレスが登録されていません。",
    "introduction": "自己紹介"
  },
  "en": {
    "warehouseLocation": "Warehouse Location",
    "warehouseLocationFormatted": "{location}",
    "basicInfo": "Basic Information",
    "operatingHours": "Operating Hours",
    "locationByCar": "Location By Car",
    "warehouseType": "Warehouse Type",
    "interchange": "Highway Interchange",
    "airport": "Airport",
    "seaport": "Seaport",
    "station": "Station",
    "minFromOrigin": "{min}min from {name}",
    "warehouseSpecs": "Warehouse Specs",
    "warehouseArea": "Warehouse Area",
    "goods": "Goods",
    "deliveryService": "Delivery Service",
    "warehouseEquipmentsAndOps": "Warehouse Equipments ＆ Operations",
    "warehouseEquipments": "Warehouse Equipments",
    "availableTruckSizes": "Truck Sizes",
    "security": "Security",
    "counterDisaster": "Counter Disaster",
    "holiday": "Closed",
    "noValidAddress": "No valid address is registered",
    "introduction": "Introduction"
  }
}
</i18n>

<template>
  <div v-if="warehouse" class="warehouse-details bg-white rounded-lg py-8 px-4 md:p-12 mt-4">
    <header class="featured-info flex justify-between items-start">
      <div class="mr-8">
        <div class="title flex flex-row flex-wrap justify-start items-end">
          <div class="text-3xl font-bold text-header">{{ warehouseTitle }}</div>
          <div class="text-lg font-bold ml-4 text-header">{{ warehouse.displayNumber }}</div>
        </div>
        <div>
          <div class="text-base flex items-center text-header">
            <span>{{ shortAddress }}</span>
            <nuxt-link v-if="this.warehouse.address.regionName" :to="{ path: localePath(`/warehouses/search/map/${this.warehouse.address.regionName}/?pin_id=${warehouse.displayNumber}`) }" class="inline-flex items-center h-5 ml-2 link-map bg-secondary text-white px-2 rounded text-xs font-medium">
              MAP
            </nuxt-link>
          </div>
          <div class="text-xs mt-2 text-header">{{ locationsSummary }}</div>
        </div>
      </div>
      <div class="flex">
        <div v-if="hasFreeSpaceInfo && showFreespace" class="availability ml-4 border-orange border-3 p-2 leading-4">
            <div class="text-xs font-bold text-secondary">
              {{ freeSpace.effectivePeriod }}
            </div>
            <div v-if="freeSpace.spaceAvailable <= 1000" class="whitespace-no-wrap">
              <span class="text-xl font-bold text-header">{{ freeSpace.spaceAvailable }}</span><span class="ml-1 text-xxs">{{ $t('warehouse.tsuboAvailable') }}</span>
            </div>
            <div v-else class="whitespace-no-wrap">
              <span class="text-xl font-bold text-header">1000</span>
              <span class="text-xxs text-header">{{ $t('warehouse.tsubo') }}</span><span class="ml-1 text-xxs font-bold text-header">{{ $t('warehouse.greaterThan' ) }}</span>
            </div>
          </div>
          <div v-else-if="showFreespace" class="availability ml-4 border-orange border-3 p-2 leading-4">
            <div class="text-sm font-bold text-header">{{ $t('warehouse.freeSpaceInfo') }}</div>
            <div class="text-xl font-bold text-header">{{ $t('warehouse.inquire') }}</div>
          </div>
      </div>
    </header>
    <section class="visual flex flex-row bg-white pb-4 md:pt-4 md:pb-8 mt-2">
      <div class="map w-full h-64 bg-gray-400 border border-gray-200 rounded-lg overflow-hidden">
        <gmap-map
          v-if="latlng"
          ref="gmap"
          :center="latlng"
          :zoom="10"
          :options="mapOptions"
          class="border-none"
        >
          <gmap-marker
            :position="latlng"
            :clickable="false"
            :draggable="false"
            :icon="{url: require('~/assets/img/marker_green.svg')}"
          />
        </gmap-map>
        <div v-else>
          {{ $t('noValidAddress') }}
        </div>
      </div>
    </section>
    <div v-if="hasIntroduction" class="bg-white rounded-lg mt-4">
      <section class="basic-info">
        <h2 class="info-section-header">
          {{ $t('introduction') }}
        </h2>
        <section class="visual my-10">
          <div class="relative">
            <thumbnail-image-gallery v-if="photoUrls.length > 0" :images="photoUrls" />
          </div>
        </section>
        <section>
          <div v-if="warehouse.promoTitleDisplayed" class="text-2xl font-bold text-header">{{ warehouse.promoTitle }}</div>
          <div class="items mt-3 mb-2 flex flex-wrap row-gap-2"><span v-for="(item, index) in supportedCargo" :key="index" class="tag">{{ item }}</span></div>
          <div v-if="warehouse.promoContentsDisplayed" class="break-all whitespace-pre-wrap">{{ warehouse.promoContent }}</div>
        </section>
      </section>
    </div>
    <div class="bg-white rounded-lg mt-4">
      <section class="basic-info">
        <h2 class="info-section-header">
          {{ $t('basicInfo') }}
        </h2>
        <div class="flex flex-row justify-start content-start flex-wrap">
          <div class="operating-hours info-column">
            <div class="badge-plain">
              {{ $t('operatingHours') }}
            </div>
            <table>
              <tr v-for="item in operatingHoursItems" :key="item.period">
                <th class="text-bold text-left pr-4">
                  {{ $t(`warehouse.${item.period}`) }}
                </th>
                <td v-if="item.holiday">
                  {{ $t('holiday') }}
                </td>
                <td v-else>
                  <span>{{ item.startTime }}</span>
                  <span> - </span>
                  <span>{{ item.endTime }}</span>
                </td>
              </tr>
            </table>
            <p v-if="operatingTimesNote" class="text-sm mt-3">
              <span class="whitespace-pre-line">{{ operatingTimesNote }}</span>
            </p>
          </div>
          <div class="location-by-car info-column">
            <div class="badge-plain">
              {{ $t('locationByCar') }}
            </div>
            <table>
              <tr v-for="item in locationItems" :key="item.id">
                <th class="text-bold text-left pr-4 whitespace-no-wrap align-top">
                  {{ $t(item.type) }}
                </th>
                <td>{{ $t('minFromOrigin', {name: item.name, min: item.distance}) }}</td>
              </tr>
            </table>
          </div>
          <div v-if="!warehouse.private" class="warehouse-type info-column">
            <div class="badge-plain">
              {{ $t('warehouseType') }}
            </div>
            <div>{{ $t(`warehouse.${warehouse.type}`) }}</div>
          </div>
        </div>
      </section>
      <section class="warehouse-specs mt-8">
        <h2 class="info-section-header">
          {{ $t('warehouseSpecs') }}
        </h2>
        <div class="flex flex-row justify-start content-start flex-wrap">
          <div class="area info-column">
            <div class="badge-plain">
              {{ $t('warehouseArea') }}
            </div>
            <list-options :options="propertyTypes" :selections="warehouse.propertyTypes" />
            <p v-if="!isEmpty(warehouse.propertyTypesOther)" class="mt-4 ">
              <span class="whitespace-pre-line">{{ warehouse.propertyTypesOther }}</span>
            </p>
          </div>
          <div class="goods info-column">
            <div class="badge-plain">
              {{ $t('goods') }}
            </div>
            <list-options :options="itemTypes" :selections="warehouse.itemTypes" />
            <p v-if="!isEmpty(warehouse.itemTypeOther)" class="mt-4">
              <span class="whitespace-pre-line">{{ warehouse.itemTypeOther }}</span>
            </p>
          </div>
          <div class="temperature info-column">
            <div class="badge-plain">
              {{ $t('warehouse.temperature') }}
            </div>
            <list-options :options="temperatureTypes" :selections="[warehouse.temperatureControl]" />
            <p v-if="warehouse.temperatureControlNote" class="mt-4 whitespace-pre-line">
              <span class="whitespace-pre-line">{{ warehouse.temperatureControlNote }}</span>
            </p>
          </div>
          <div class="delivery-methods info-column">
            <div class="badge-plain">
              {{ $t('deliveryService') }}
            </div>
            <list-options :options="deliveryTypes" :selections="warehouse.deliveryTypes">
              <div v-if="selfDeliveryDetails.length > 0" slot="self" class="bg-green-100 p-2 mb-1 text-xs">
                <div>{{ $t('warehouse.deliveryDetailUnitNode') }}</div>
                <ol class="list-disc ml-4 mt-1">
                  <li v-for="(dd, index) in selfDeliveryDetails" :key="index">
                    <span>{{ dd.truckSize }}:</span><span class="ml-2">{{ formatCurrency(dd.price * warexDeliveryMargin) }}{{ $t('warehouse.yen') }}</span>
                  </li>
                </ol>
              </div>
              <div v-if="charterDeliveryDetails.length > 0" slot="charter" class="bg-green-100 p-2 mb-1 text-xs">
                <div>{{ $t('warehouse.deliveryDetailUnitNode') }}</div>
                <ol class="list-disc ml-4 mt-1">
                  <li v-for="(dd, index) in charterDeliveryDetails" :key="index">
                    <span>{{ dd.truckSize }}:</span><span class="ml-2">{{ formatCurrency(dd.price * warexDeliveryMargin) }}{{ $t('warehouse.yen') }}</span>
                  </li>
                </ol>
              </div>
            </list-options>
            <p v-if="!isEmpty(warehouse.deliveryTypesOther)" class="mt-4 whitespace-pre-line">
              <span class="whitespace-pre-line">{{ warehouse.deliveryTypesOther }}</span>
            </p>
          </div>
        </div>
      </section>
      <section class="warehouse-equipments mt-8">
        <h2 class="info-section-header">
          {{ $t('warehouseEquipmentsAndOps') }}
        </h2>
        <div class="flex flex-row justify-between content-start flex-wrap">
          <div class="area info-column">
            <div class="badge-plain">
              {{ $t('warehouseEquipments') }}
            </div>
            <list-options :options="equipmentTypes" :selections="warehouse.equipments" />
            <p v-if="hasOther(warehouse.equipments)" class="ml-12 mt-2">
              <span class="whitespace-pre-line">{{ warehouse.equipmentsOther }}</span>
            </p>
          </div>
          <div class="goods info-column">
            <div class="badge-plain">
              {{ $t('availableTruckSizes') }}
            </div>
            <list-options :options="truckSizeTypes" :selections="warehouse.truckSizes" />
            <p v-if="hasOther(warehouse.truckSizes)" class="ml-12 mt-2">
              <span class="whitespace-pre-line">{{ warehouse.truckSizesOther }}</span>
            </p>
          </div>
          <div class="temperature info-column">
            <div class="badge-plain">
              {{ $t('security') }}
            </div>
            <list-options :options="securityTypes" :selections="warehouse.security" />
            <p v-if="hasOther(warehouse.security)" class="ml-12 mt-2">
              <span class="whitespace-pre-line">{{ warehouse.securityTypesOther }}</span>
            </p>
          </div>
          <div class="delivery-methods info-column">
            <div class="badge-plain">
              {{ $t('counterDisaster') }}
            </div>
            <list-options :options="counterDisasterTypes" :selections="warehouse.counterDisaster" />
            <p v-if="hasOther(warehouse.counterDisaster)" class="ml-12 mt-2">
              <span class="whitespace-pre-line">{{ warehouse.counterDisasterTypesOther }}</span>
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.availability
  min-width: 120px
  border-radius: 4px
.location
  max-width: 57%
.displayNumber
  max-width: 47%
  margin-left: auto
  @screen md
    @apply ml-16
.map
  @apply relative
  &::after
    content: ''
    display: block
    padding-bottom: 62.5%
.vue-map-container
  @apply absolute inset-0
.list-options
  img
    @apply w-3 h-3
  @media (min-width: theme('screens.md'))
    img
      width: 0.8em
      height: 0.8em
.info-section-header
  @apply font-semibold border-b
  @media (min-width: theme('screens.md'))
    @apply text-xl
.info-column
  @apply w-full mt-8 text-xs
  @media (min-width: 768px)
    @apply w-1/2 mt-8 text-base
  @media (min-width: 1048px)
    @apply w-1/3
  &.unit-types
    min-width: 455px
.tag
    @apply bg-gray-200 text-sm px-2 py-1 mr-2 rounded whitespace-no-wrap
</style>
<style>
.badge-filled,
.badge-plain {
  border-radius: 99999px;
  padding: 0 1em;
  line-height: 2;
  margin-bottom: 1em;
  display: inline-block;
  @apply whitespace-no-wrap;
  @media (min-width: theme('screens.md')) {
    font-size: 80%;
  }
}
.badge-filled {
  background-color: theme('colors.secondary.default');
  @apply text-sm text-white mb-3;
  @screen md {
    @apply text-base;
  }
}
.badge-plain {
  border: 1px solid gray;
}
</style>

<script>
import { warehouseTypes, opratingHoursTypes, locationAccessTypes, propertyTypes, itemTypes, temperatureTypes,
  deliveryTypes, equipmentTypes, truckSizeTypes, securityTypes, counterDisasterTypes, freeSpaceDisplayMap } from '@/model/warehouse';
import { photoUrl } from '@/components/utils/photo';
import { isEmpty } from '@/components/utils/string';
import { getToday, convertToDate, formatDate, addMonths } from '@/components/utils/date';
import styles from '@/components/warehouses/search/map/mapStyles';
import ListOptions from './listOptions';
import ThumbnailImageGallery from '@/components/thumbnailImageGallery';

export default {
  components: {
    ListOptions, ThumbnailImageGallery
  },
  props: {
    warehouse: { type: Object, required: true },
    showFreespace: { type: Boolean, default: true },
  },
  data () {
    return {
      isEmpty,
      warehouseTypes,
      opratingHoursTypes,
      locationAccessTypes,
      propertyTypes,
      itemTypes,
      temperatureTypes,
      deliveryTypes,
      equipmentTypes,
      truckSizeTypes,
      securityTypes,
      counterDisasterTypes,
      mapOptions: {
        disableDefaultUI: true,
        scrollwheel: false,
        scaleControl: false,
        disableDoubleClickZoom: true,
        scrollGesturesEnabled: false,
        gestureHandling: 'greedy',
        draggable: false,
        styles
      }
    };
  },
  computed: {
    warehouseTitle () {
      return this.$t('warehouseLocationFormatted', { location: this.shortAddress });
    },
    shortAddress () {
      return `${this.warehouse.address.regionName} ${this.warehouse.address.muniName}`;
    },
    address () {
      const { address } = (this.warehouse || {})
      return address
    },
    price () {
      return this.warehouse.priceSpecifications[0];
    },
    latlng () {
      if (!this.warehouse.address) return null;
      if (!this.warehouse.address.lat || !this.warehouse.address.lng) return null;
      return { lat: this.warehouse.address.lat, lng: this.warehouse.address.lng };
    },
    operatingHoursItems () {
      return this.opratingHoursTypes.reduce((options, period) => {
        const entry = this.warehouse.operatingTimes.find(ot => ot.period === period);
        if (entry) options.push(entry);
        return options;
      }, []);
    },
    locationItems () {
      return this.locationAccessTypes.reduce((options, type) => {
        const entry = this.warehouse.relativeLocations.find(rl => rl.type === type);
        if (entry) options.push(entry);
        return options;
      }, []);
    },
    locationsSummary () {
      return this.locationItems.map(loc => this.$t('minFromOrigin', { name: loc.name, min: loc.distance })).join(this.$t('warehouse.comma-space'));
    },
    operatingTimesNote () {
      const other = this.warehouse.operatingTimes.find(ot => ot.period === 'other');
      return other ? other.notes : null;
    },
    hasFreeSpaceInfo () {
      return !(this.freeSpace === null);
    },
    freeSpace () {
      const defaultFs = this.warehouse.freeSpaces.find(({ isDefault }) => (isDefault));
      if (!defaultFs) {
        this.showFreespace = false;
        return null;
      }

      const { effectiveAt, freeSpace } = defaultFs;
      const today = getToday().getTime();
      const dateEffective = convertToDate(effectiveAt).getTime();
      const expiringDate = addMonths(dateEffective, 3).getTime();
      // freeSpace is bigger than 0-50 tsubo (unknown is -1)
      //  within effectiveAt includes today to 3 months later (expiringDate) if the effective date is current
      //   OR the future date
      if (freeSpace >= 0 && ((dateEffective <= today && today <= expiringDate) || dateEffective > today)) {
        return {
          effectivePeriod: dateEffective > today ? `${formatDate(dateEffective)} ~` : this.$t('warehouse.currentlyAvailable'),
          spaceAvailable: freeSpaceDisplayMap[freeSpace]
        };
      } else return null;
    },
    photoUrls () {
      const convertToWeight = type => (type === 'promo' ? 2 : type === 'interior' ? 1 : 0); // in order of 'exterior' > 'interior' > 'promo'

      return this.warehouse.photos.slice()
        .sort((a, b) => ((convertToWeight(a.photoType) - convertToWeight(b.photoType)) || (a.sortOrder - b.sortOrder)))
        .filter(({ photoType }) => photoType !== 'promo' || (photoType === 'promo' && this.warehouse.promoPhotosDisplayed))
        .map(p => (this.formatPhotoUrl(p.url)));
    },
    supportedCargo () {
      return this.warehouse.supportedCargo.map(item => (this.$t(`cargoType.${item}`)));
    },
    hasIntroduction () {
      return (this.warehouse.promoTitleDisplayed && this.warehouse.promoTitle)
        || (this.warehouse.promoContentsDisplayed && this.warehouse.promoContent)
        || (this.photoUrls.length > 0);
    },
    selfDeliveryDetails () {
      return this.getDeliveryDetails('self');
    },
    charterDeliveryDetails () {
      return this.getDeliveryDetails('charter');
    },
    warexDeliveryMargin () {
      return 1 + this.$store.getters['dynConfig/warexDeliveryMargin'];
    },
  },
  methods: {
    hasOther (values) {
      return values && values.includes('other');
    },
    formatPhotoUrl (url) {
      if (url) return photoUrl(url);
      return require('~/assets/img/logotype_warex@3x.png');
    },
    getDeliveryDetails (type) {
      return this.warehouse.deliveryDetails.reduce((results, dd) => {
        if (type === dd.type) {
          // if only type exists, ignore it
          if (dd.truckSize && dd.price) {
            results.push(dd);
          }
        }
        return results;
      }, []);
    },
    formatCurrency (number) {
      if (number) {
        return new Intl.NumberFormat('ja-JP').format(number);
      }
      return '';
    }
  }
};
</script>
